// src/components/MessageReactions.tsx
// Quick emoji reactions for chat messages

import React, { useState } from 'react';
import { IonButton, IonIcon, IonPopover } from '@ionic/react';
import { add } from 'ionicons/icons';
import './MessageReactions.css';

interface Reaction {
  emoji: string;
  users: string[]; // user IDs who reacted
  count: number;
}

interface MessageReactionsProps {
  reactions: Reaction[];
  currentUserId: string;
  onAddReaction: (emoji: string) => void;
  onRemoveReaction: (emoji: string) => void;
  isActive?: boolean;
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎲', '🎮', '🔥'];

export const MessageReactions: React.FC<MessageReactionsProps> = ({
  reactions,
  currentUserId,
  onAddReaction,
  onRemoveReaction,
  isActive = false,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState<any>(null);

  const handleReactionClick = (emoji: string, userReacted: boolean) => {
    // Only allow removing reactions that the user added
    if (userReacted) {
      onRemoveReaction(emoji);
    }
    // If user hasn't reacted, clicking does nothing (use + button to add)
  };

  const handleAddClick = (e: any) => {
    setPopoverEvent(e);
    setShowPicker(true);
  };

  return (
    <div className="message-reactions">
      {/* Existing reactions */}
      {reactions.map((reaction, index) => {
        const userReacted = reaction.users.includes(currentUserId);
        return (
          <button
            key={reaction.emoji}
            className={`reaction-bubble ${userReacted ? 'user-reacted' : ''}`}
            onClick={() => handleReactionClick(reaction.emoji, userReacted)}
            data-reaction-index={index}
            style={{ cursor: userReacted ? 'pointer' : 'default' }}
          >
            <span className="reaction-emoji">{reaction.emoji}</span>
            {reaction.count > 1 && (
              <span className="reaction-count">{reaction.count}</span>
            )}
          </button>
        );
      })}

      {/* Add reaction button - only show when message is active */}
      {isActive && (
        <button className="add-reaction-btn" onClick={handleAddClick}>
          <IonIcon icon={add} />
        </button>
      )}

      {/* Emoji picker popover */}
      <IonPopover
        isOpen={showPicker}
        event={popoverEvent}
        onDidDismiss={() => setShowPicker(false)}
        size="auto"
      >
        <div className="emoji-picker">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className="emoji-option"
              onClick={() => {
                onAddReaction(emoji);
                setShowPicker(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      </IonPopover>
    </div>
  );
};
